import { createContext, ReactNode, useContext, useRef, useState } from 'react'
import { AnimationState, InfoSnackbarType } from '../constants/animationStates'

interface ContextType {
  snackbarText: string
  snackbarVisibilityState: AnimationState
  showSnackbar: (message: string, durationInSec?: number) => void
  hideSnackbar: () => void
  disabledSnackbars: InfoSnackbarType[]
  disableSnackbar: (snackbar: InfoSnackbarType) => void
}

interface Props {
  children: ReactNode
}

const Context = createContext({} as ContextType)

const SnackbarContext = ({ children }: Props) => {
  const [snackbarVisibilityState, setSnackbarVisibilityState] = useState<AnimationState>(
    AnimationState.UNSET,
  )
  const [snackbarText, setSnackbarText] = useState<string>('')
  const [disabledSnackbars, setDisabledSnackbars] = useState<InfoSnackbarType[]>([])
  const pauseTimer = useRef<NodeJS.Timeout | undefined>(undefined)
  const unsetTimer = useRef<NodeJS.Timeout | undefined>(undefined)

  const showSnackbar = (message: string, durationInSec = 5) => {
    setSnackbarVisibilityState(AnimationState.PLAY)
    setSnackbarText(message)
    pauseTimer.current = setTimeout(() => {
      setSnackbarVisibilityState(AnimationState.PAUSE)
    }, durationInSec * 1000 - 300)
    unsetTimer.current = setTimeout(() => {
      setSnackbarVisibilityState(AnimationState.UNSET)
    }, durationInSec * 1000)
  }

  const hideSnackbar = () => {
    setSnackbarVisibilityState(AnimationState.UNSET)
    setSnackbarText('')
    clearTimeout(pauseTimer.current)
    clearTimeout(unsetTimer.current)
    pauseTimer.current = undefined
    unsetTimer.current = undefined
  }

  const disableSnackbar = (snackbar: InfoSnackbarType) => {
    setDisabledSnackbars(current => (current.includes(snackbar) ? current : [...current, snackbar]))
  }

  return (
    <Context.Provider
      value={{
        snackbarText,
        snackbarVisibilityState,
        showSnackbar,
        hideSnackbar,
        disabledSnackbars,
        disableSnackbar,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useContextSnackbar = () => {
  return useContext(Context)
}

export default SnackbarContext
